import React, { useCallback, useContext, useState } from 'react'
import { useSelector } from 'react-redux'

import { Box, Image } from '@campaignhub/suit-theme'
import PageContext from '@contexts/pageContext'
import PropTypes from 'prop-types'

import FilePreviewOverlay from '@components/FilePreviewOverlay'

const isVideoAsset = asset => asset?.file?.fileType?.name?.toLowerCase() === 'mp4'

const assetPreviewUrl = (asset, isPhotographyJob) => {
  if (isPhotographyJob) {
    return asset?.file?.downloadUrl || ''
  }

  return asset?.file?.previewGeneratedAt ? asset.premiumUrl || asset.previewUrl : ''
}

const toPreviewItem = (asset, isPhotographyJob) => {
  const isVideo = isVideoAsset(asset)

  return {
    key: asset.id,
    name: asset?.file?.filename || asset?.file?.displayName || asset?.file?.originalName,
    downloadName: asset?.file?.filename || asset?.file?.originalName
      || (asset?.assetUrl || '').split(/[?#]/)[0].split('/').pop(),
    previewUrl: assetPreviewUrl(asset, isPhotographyJob),
    videoUrl: isVideo ? asset?.assetUrl : null,
    downloadUrl: isVideo ? null : (asset?.file?.downloadUrl || asset?.assetUrl),
    href: `/service-jobs/${asset.serviceJobId}/assets?assetId=${asset.id}`,
    // The lowered-resolution notice belongs to the Resources/Related previews; asset previews
    // (Premium for standard jobs, full-res for Photography) don't carry it.
    reducedResolution: false,
  }
}

// The Assets/Proofs tabs open this from the Gallery grid. It adapts the redux asset list into the
// shared preview item shape so the rendering itself lives in FilePreviewOverlay.
const GalleryOverlay = ({ isPhotographyJob = false }) => {
  const [selectedId, setSelectedId] = useState()

  const { assets } = useSelector(reduxState => reduxState.entities)
  const {
    callbacks: { toggleGalleryOverlay },
    initialAssetId,
  } = useContext(PageContext)

  const assetList = Object.values(assets)
  const requestedId = selectedId !== undefined ? selectedId : initialAssetId
  const selectedAsset = assetList.find(asset => asset.id === requestedId) || assetList[0]
  const validAssets = assetList.filter(asset => asset.serviceJobId === selectedAsset?.serviceJobId)

  const items = validAssets.map(asset => toPreviewItem(asset, isPhotographyJob))
  const selectedItem = items.find(item => item.key === requestedId) || items[0]

  const onNavigate = useCallback(item => setSelectedId(item.key), [])

  if (!selectedItem) {
    return null
  }

  return (
    <>
      <FilePreviewOverlay
        item={selectedItem}
        items={items}
        onClose={() => toggleGalleryOverlay()}
        onNavigate={onNavigate}
      />
      {/* Hidden pre-loaders for all images to improve navigation performance */}
      {isPhotographyJob && (
        <Box position="absolute" width={0} height={0} overflow="hidden" opacity={0}>
          {validAssets.map((asset) => {
            if (!isVideoAsset(asset)) {
              return (
                <Image
                  key={`preload-${asset.id}`}
                  url={asset?.file?.downloadUrl || ''}
                />
              )
            }
            return null
          })}
        </Box>
      )}
    </>
  )
}

GalleryOverlay.propTypes = {
  isPhotographyJob: PropTypes.bool,
}

export default GalleryOverlay
